import { Typography, type TypographyProps } from "@mantine/core";
import clsx from "clsx";
import { useMemo, type FC } from "react";

import { highlightRichText } from "./highlightRichText";
import classes from "./RichTextContent.module.css";

const richTextTagPattern =
    /<\/?(?:p|br|strong|b|em|i|u|s|strike|del|a|span|mark|sup|sub|h1|h2|h3|h4|h5|h6|ul|ol|li|blockquote|pre|code|hr)(?:\s[^>]*)?\s*\/?>/i;
const allowedTags = new Set([
    "a",
    "b",
    "blockquote",
    "br",
    "code",
    "del",
    "em",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "hr",
    "i",
    "li",
    "mark",
    "ol",
    "p",
    "pre",
    "s",
    "span",
    "strong",
    "strike",
    "sub",
    "sup",
    "u",
    "ul"
]);
const removedTags = new Set([
    "embed",
    "iframe",
    "link",
    "math",
    "meta",
    "object",
    "script",
    "style",
    "svg"
]);
const alignedTags = new Set(["p", "h1", "h2", "h3", "h4", "h5", "h6"]);
const textAlignValues = new Set(["left", "center", "right", "justify"]);
const mergeFieldIdPattern = /^[A-Za-z_][A-Za-z0-9_]*$/;
const safeColourPattern = /^(?:#[0-9a-f]{3,8}|rgba?\([0-9.,%\s]+\)|hsla?\([0-9.,%\s]+\)|[a-z]+)$/i;

export type RichTextMergeValues = Readonly<Record<string, string>>;

export type PreparedRichTextContent = Readonly<{
    html: string;
    text: string;
}>;

export type RichTextContentProps = Omit<TypographyProps, "children"> & {
    content?: string | null;
    mergeValues?: RichTextMergeValues;
    preparedContent?: PreparedRichTextContent;
    /** Words to mark wherever they appear in the text, such as a search's terms. */
    highlight?: readonly string[];
};

/**
 * Replaces a parsed document body with a paragraph containing literal text and explicit line breaks.
 * Postconditions: Each newline after the first produces a br; markup characters remain text.
 * Assumptions: The supplied document is a disposable DOM document; its body is mutated.
 */
const createPlainTextContent = (document: Document, content: string) => {
    const paragraph = document.createElement("p");
    const lines = content.split(/\r?\n/);

    lines.forEach((line, index) => {
        if (index > 0) {
            paragraph.appendChild(document.createElement("br"));
        }
        paragraph.appendChild(document.createTextNode(line));
    });

    document.body.replaceChildren(paragraph);
};

const isSafeColour = (value: string) => safeColourPattern.test(value.trim());

/**
 * Copies a permitted link destination and its existing external-window safeguards.
 * Postconditions: Missing, malformed or disallowed-protocol destinations are not copied.
 * Accepted destinations retain their trimmed spelling; external HTTP or explicit blank targets
 * request a new browsing context. URL failures are caught and leave no destination.
 * Assumptions: Browser location provides the base origin; the caller supplies a new target element.
 */
const copySafeLink = (source: HTMLElement, target: HTMLElement) => {
    const href = source.getAttribute("href")?.trim();
    if (!href) {
        return;
    }

    try {
        const url = new URL(href, window.location.origin);
        if (!["http:", "https:", "mailto:", "tel:"].includes(url.protocol)) {
            return;
        }

        target.setAttribute("href", href);
        target.setAttribute("rel", "noopener noreferrer");

        const isExternalHttp =
            ["http:", "https:"].includes(url.protocol) && url.origin !== window.location.origin;
        if (source.getAttribute("target") === "_blank" || isExternalHttp) {
            target.setAttribute("target", "_blank");
        }
    } catch {
        // Malformed links are deliberately rendered without a destination.
    }
};

/**
 * Appends the allowed projection of one DOM node into the destination tree.
 * Postconditions: Removed elements lose their descendants; unsupported elements retain eligible
 * children. Accepted elements copy only the supported link, alignment, color and merge-field data.
 * Merge values become text nodes with label/text fallback; recursion preserves child order.
 * Assumptions: Browser DOM globals exist and nodes belong to a compatible HTML realm. This mutates
 * parent and constructs nodes in document; it does not mutate the source node.
 */
const appendSanitisedNode = (
    parent: Node,
    source: Node,
    document: Document,
    mergeValues?: RichTextMergeValues
) => {
    if (source.nodeType === Node.TEXT_NODE) {
        parent.appendChild(document.createTextNode(source.textContent ?? ""));
        return;
    }

    if (!(source instanceof HTMLElement)) {
        return;
    }

    const tagName = source.tagName.toLowerCase();
    if (removedTags.has(tagName)) {
        return;
    }

    if (!allowedTags.has(tagName)) {
        source.childNodes.forEach(child =>
            appendSanitisedNode(parent, child, document, mergeValues)
        );
        return;
    }

    const mergeFieldId = source.getAttribute("data-merge-field")?.trim();
    const isMergeField =
        tagName === "span" && !!mergeFieldId && mergeFieldIdPattern.test(mergeFieldId);

    if (isMergeField && mergeValues) {
        const fallback = source.getAttribute("data-merge-field-label") ?? source.textContent ?? "";
        parent.appendChild(document.createTextNode(mergeValues[mergeFieldId] ?? fallback));
        return;
    }

    const element = document.createElement(tagName);

    if (isMergeField) {
        element.className = "tmd-merge-field";
        element.setAttribute("data-merge-field", mergeFieldId);
        const label = source.getAttribute("data-merge-field-label");
        if (label) {
            element.setAttribute("data-merge-field-label", label);
        }
    }

    if (alignedTags.has(tagName) && textAlignValues.has(source.style.textAlign)) {
        element.style.textAlign = source.style.textAlign;
    }

    if (tagName === "span" && source.style.color && isSafeColour(source.style.color)) {
        element.style.color = source.style.color;
    }

    if (
        tagName === "mark" &&
        source.style.backgroundColor &&
        isSafeColour(source.style.backgroundColor)
    ) {
        element.style.backgroundColor = source.style.backgroundColor;
        const dataColour = source.getAttribute("data-color")?.trim();
        if (dataColour && isSafeColour(dataColour)) {
            element.setAttribute("data-color", dataColour);
        }
    }

    if (tagName === "a") {
        copySafeLink(source, element);
    }

    parent.appendChild(element);
    source.childNodes.forEach(child => appendSanitisedNode(element, child, document, mergeValues));
};

/**
 * Builds supported rich-text HTML and a whitespace-normalized text projection.
 * Postconditions: Falsy content returns empty strings. Content without a recognized rich-text tag
 * is treated as literal text; otherwise the parsed body is rebuilt through the allowed-node rules.
 * Assumptions: DOMParser, Node, HTMLElement and window are available for nonempty content.
 * Unexpected DOM failures propagate; input is not inserted into the live document here.
 */
export const prepareRichTextContent = (
    content: string | null | undefined,
    mergeValues?: RichTextMergeValues
): PreparedRichTextContent => {
    if (!content) {
        return { html: "", text: "" };
    }

    const sourceDocument = new DOMParser().parseFromString(content, "text/html");
    if (!richTextTagPattern.test(content)) {
        createPlainTextContent(sourceDocument, content);
    }

    const container = sourceDocument.createElement("div");
    sourceDocument.body.childNodes.forEach(child =>
        appendSanitisedNode(container, child, sourceDocument, mergeValues)
    );

    return {
        html: container.innerHTML,
        text: (container.textContent ?? "").replace(/\s+/g, " ").trim()
    };
};

/**
 * Renders prepared rich text through the shared typography presentation.
 * Postconditions: Empty prepared HTML renders nothing; otherwise forwarded props and classes are
 * applied and the selected HTML is inserted by React. Preparation is memoized by input identity.
 * Highlight terms are marked in the prepared text only, after sanitising.
 * Preconditions: Supplied preparedContent must already be trusted or sanitized: it bypasses
 * prepareRichTextContent entirely. Browser DOM support is required when preparation runs.
 */
const RichTextContent: FC<RichTextContentProps> = ({
    content,
    mergeValues,
    preparedContent,
    highlight,
    className,
    ...props
}) => {
    // Prepared content is an explicit bypass; callers own its HTML safety.
    const prepared = useMemo(
        () => preparedContent ?? prepareRichTextContent(content, mergeValues),
        [content, mergeValues, preparedContent]
    );

    // A key of the terms keeps the marked HTML stable while the caller rebuilds the same array.
    const highlightKey = highlight?.join("\u0000") ?? "";
    const html = useMemo(
        () =>
            highlightKey
                ? highlightRichText(prepared.html, highlightKey.split("\u0000"), classes.highlight)
                : prepared.html,
        [highlightKey, prepared.html]
    );

    if (!html) {
        return null;
    }

    return (
        <Typography
            {...props}
            className={clsx(classes.root, className)}
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
};

export default RichTextContent;
